// Dónde vive el plan de una sesión de Catálogo: en `agent_chats.lienzo` de la sesión (una fila de
// tipo `catalogo`), bajo la clave `catalogo`, como Diseño y Redes guardan lo suyo
// (ADR-2026-10-13-catalogo-con-el-agente). Un chat de tipo catálogo nunca tiene el lienzo del
// Agente, así que la columna es toda del plan. Autoguardado con debounce por huella.
import { useEffect, useRef } from 'react';
import { supabase } from '../../../../lib/supabase';
import { leerLienzo } from '../../agentChatApi';
import { normalizarPlan, type PlanDeCatalogo } from './plan';

/** Lo crudo de `lienzo` → el plan, o null si no es una sesión de catálogo. Puro. */
export function planDeLienzo(raw: unknown): PlanDeCatalogo | null {
  const catalogo = (raw as { catalogo?: unknown } | null)?.catalogo;
  return catalogo ? normalizarPlan(catalogo) : null;
}

export async function leerPlan(chatId: string): Promise<PlanDeCatalogo | null> {
  return planDeLienzo(await leerLienzo(chatId));
}

export async function guardarPlan(chatId: string, plan: PlanDeCatalogo): Promise<boolean> {
  const { error } = await supabase.from('agent_chats').update({ lienzo: { catalogo: plan } }).eq('id', chatId);
  if (error) {
    console.error('[catalogoAgente] guardarPlan', error);
    return false;
  }
  return true;
}

const DEBOUNCE_MS = 800;

type Temporizador = (fn: () => void, ms: number) => number;

/**
 * Guarda el plan con debounce por huella, y `vaciar()` guarda lo pendiente AL INSTANTE: es lo
 * que corre al desmontar la sesión, para que editar y navegar dentro de los 800 ms no pierda el
 * cambio (review 2026-10-14, punto 8). Lo recién leído se `recordar` para no reescribirlo.
 * Puro salvo el timer y la función de guardar, inyectables.
 */
export class Guardador<P = PlanDeCatalogo> {
  private ultimo = '';
  private pendiente: { chatId: string; plan: P; huella: string } | null = null;
  private timer: number | null = null;
  constructor(
    private readonly guardar: (chatId: string, plan: P) => Promise<boolean>,
    private readonly programarTimer: Temporizador = (fn, ms) => window.setTimeout(fn, ms),
    private readonly cancelarTimer: (id: number) => void = (id) => window.clearTimeout(id),
  ) {}

  recordar(_chatId: string, plan: P): void {
    this.ultimo = JSON.stringify(plan);
  }

  programar(chatId: string, plan: P): void {
    const huella = JSON.stringify(plan);
    if (huella === this.ultimo || huella === this.pendiente?.huella) return;
    if (this.timer !== null) this.cancelarTimer(this.timer);
    this.pendiente = { chatId, plan, huella };
    this.timer = this.programarTimer(() => void this.vaciar(), DEBOUNCE_MS);
  }

  /** Guarda lo pendiente ahora (si hay). */
  async vaciar(): Promise<void> {
    if (this.timer !== null) {
      this.cancelarTimer(this.timer);
      this.timer = null;
    }
    const p = this.pendiente;
    if (!p) return;
    this.pendiente = null;
    this.ultimo = p.huella;
    await this.guardar(p.chatId, p.plan);
  }
}

/** Guarda el plan cuando cambia (por huella), con debounce; al desmontar, lo pendiente se guarda igual. */
export function usePlanGuardado(chatId: string | undefined, plan: PlanDeCatalogo | null): void {
  const guardador = useRef<Guardador | null>(null);
  if (!guardador.current) guardador.current = new Guardador(guardarPlan);
  const primero = useRef(true);
  useEffect(() => {
    if (!chatId || !plan) return;
    if (primero.current) {
      primero.current = false;
      guardador.current!.recordar(chatId, plan);
      return;
    }
    guardador.current!.programar(chatId, plan);
  }, [chatId, plan]);
  useEffect(() => {
    const g = guardador.current!;
    return () => {
      void g.vaciar();
    };
  }, []);
}
